import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
// The agency-site "Estate" theme. Imported here rather than with an @import
// inside index.css, which has to precede every other statement in a
// stylesheet and would therefore have to sit above the @tailwind layers it
// depends on. Entirely scoped under `.fs`: it cannot reach the CRM, the
// marketing site or an existing agency page.
import "@/styles/foundationSites.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";
import { wasServerRendered } from "@/lib/ssrData";
import { captureLeadSource } from "@/lib/leadSource";

// Before anything renders or navigates: the landing URL's utm_ and click ids
// and the outside referrer are only there on this first page of the visit.
captureLeadSource();

// There is no react-query here, and there never was one in use.
//
// A QueryClientProvider used to wrap the whole app, which meant every visitor
// to an agency's website downloaded @tanstack/react-query — 84KB of source in
// the entry bundle — to supply a context that not one component read. The
// codebase has no useQuery, no useMutation, no useQueryClient: every fetch in
// it goes through lib/api. Guarded by a test, so it cannot creep back in
// unused a second time.

// Adopt the server's DOM, or build one.
//
// When the page was server-rendered, `hydrateRoot` walks the markup that is
// already there and attaches to it rather than throwing it away. That
// distinction is the whole point: `createRoot` empties #root first, so the
// server's heading is removed and React's own is painted as a NEW element,
// and a newly painted largest element restarts the LCP clock however
// identical it looks. Measured before this existed: the server drew the
// heading at 1.72s and LCP was still reported at 2.86s.
//
// Hydration is also the strict one. It demands that this first render
// produce the tree the server produced, which is why lib/ssrData.js seeds
// the brand from the same payload the server used. If they ever disagree
// React says so in the console and re-renders from scratch: the page is
// correct either way, it just costs what it cost before.
const container = document.getElementById("root");
const tree = (
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
if (wasServerRendered() && container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, tree, {
    // A hydration failure is silent by default: React logs a minified error
    // number, rebuilds the tree from scratch, and the page looks perfectly
    // correct while every bit of the benefit is gone. That is the worst
    // shape a regression can have, so it says so out loud, with the part of
    // the tree it happened in.
    onRecoverableError: (error, errorInfo) => {
      const detail = (errorInfo && errorInfo.componentStack) || "";
      // eslint-disable-next-line no-console
      console.error("[ssr] hydration fell back to client rendering:",
                    (error && error.message) || error, detail);
      try {
        window.__ssrHydrationError = {
          message: String((error && error.message) || error),
          componentStack: detail,
        };
      } catch (e) { /* nothing to do about it */ }
    },
  });
} else {
  ReactDOM.createRoot(container).render(tree);
}
